// One exchange-rate table for the whole app (Finance, Costing, Landed Costing): dated rates per currency pair
// (entered or imported from CSV), and fixed rates for pegged currencies (e.g. 1 USD = 3.75 SAR) used on every date
// unless a rate is entered for that exact date. A pair without its own rate is worked out the other way round, or
// through the local currency.

export interface RateRow { from: string; to: string; rate: number; date: string | null; fixed: boolean }

export class RateBook {
  private byPair = new Map<string, RateRow[]>();
  constructor(rows: RateRow[], public local: string) {
    for (const r of rows) {
      if (!(r.rate > 0)) continue;
      const k = `${r.from}>${r.to}`;
      const list = this.byPair.get(k) || [];
      list.push(r);
      this.byPair.set(k, list);
    }
    for (const list of this.byPair.values()) list.sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
  }
  /** The rate of one pair as entered (no inverse, no cross), for a date (or the latest). */
  private direct(from: string, to: string, date?: string | null): number | null {
    const list = this.byPair.get(`${from}>${to}`);
    if (!list?.length) return null;
    const dated = list.filter((r) => !r.fixed && r.date);
    const fixed = list.find((r) => r.fixed);
    if (date) {
      const exact = dated.find((r) => r.date === date);
      if (exact) return exact.rate;
      if (fixed) return fixed.rate;
      const before = dated.filter((r) => r.date! <= date);
      if (before.length) return before[before.length - 1].rate;
      return dated[0]?.rate ?? null;
    }
    if (fixed) return fixed.rate;
    return dated.length ? dated[dated.length - 1].rate : null;
  }
  private pair(from: string, to: string, date?: string | null): number | null {
    const d = this.direct(from, to, date);
    if (d) return d;
    const inv = this.direct(to, from, date);
    return inv ? 1 / inv : null;
  }
  /** How many `to` one `from` is worth on a date (latest when no date); null when unknown. */
  rate(from: string, to: string, date?: string | null): number | null {
    if (!from || !to) return null;
    if (from === to) return 1;
    const p = this.pair(from, to, date);
    if (p) return round6(p);
    // Through the local currency, then through USD.
    for (const via of [this.local, "USD"]) {
      if (via === from || via === to) continue;
      const a = this.pair(from, via, date), b = this.pair(via, to, date);
      if (a && b) return round6(a * b);
    }
    return null;
  }
  convert(amount: number, from: string, to: string, date?: string | null): number | null {
    const r = this.rate(from, to, date);
    return r === null ? null : Math.round(amount * r * 100) / 100;
  }
  /** Latest date that has any rate (for "rates as of"). */
  latestDate(): string {
    let d = "";
    for (const list of this.byPair.values()) for (const r of list) if (r.date && r.date > d) d = r.date;
    return d;
  }
}
const round6 = (n: number) => Math.round(n * 1e8) / 1e8;

/** Parse a CSV of rates: from,to,date,rate (header optional; date blank or "fixed" for a fixed rate). */
export function parseRatesCsv(text: string): { rows: RateRow[]; errors: string[] } {
  const rows: RateRow[] = [], errors: string[] = [];
  const lines = text.replace(/^﻿/, "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  lines.forEach((line, i) => {
    const cells = line.split(/[,;\t]/).map((c) => c.trim().replace(/^"|"$/g, ""));
    if (i === 0 && /^from/i.test(cells[0])) return;
    const [from, to, date, rate] = cells;
    const r = Number(String(rate || "").replace(",", "."));
    const fixed = !date || /^fixed$/i.test(date);
    const iso = fixed ? null : toIso(date);
    if (!/^[A-Z]{3}$/i.test(from || "") || !/^[A-Z]{3}$/i.test(to || "") || !(r > 0) || (!fixed && !iso)) { errors.push(`Line ${i + 1}: "${line}" — expected FROM,TO,date,rate (e.g. USD,SAR,2026-09-01,3.75)`); return; }
    rows.push({ from: from.toUpperCase(), to: to.toUpperCase(), date: iso, rate: r, fixed });
  });
  return { rows, errors };
}
function toIso(d: string): string | null {
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(d);
  return m ? `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}` : null;
}
